function activityTimestamp(item) {
  const candidates = [item.at, item.occurred_at, item.created_at, item.completed_at, item.due_at];
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const value = new Date(candidate);
    if (!Number.isNaN(value.getTime())) {
      return value;
    }
  }
  return new Date(0);
}

function summarizeMessage(message) {
  return {
    kind: "message",
    label: message.direction === "outbound" ? "Reply sent" : "Inbound message",
    value: message.direction || "message",
    tone: message.direction === "outbound" ? "ready" : "stale",
    meta: `${message.created_at || "timestamp unavailable"} | ${message.body || "Body unavailable"}`,
    at: activityTimestamp(message),
  };
}

function summarizeQueueAction(item) {
  return {
    kind: "queue_action",
    label: item.summary || item.action_type || "Queue activity",
    value: item.source_kind || "manual",
    tone: item.source_kind === "automation" ? "stale" : "ready",
    meta: item.occurred_at || "timestamp unavailable",
    at: activityTimestamp(item),
  };
}

function summarizeTimelineEvent(item) {
  return {
    kind: "timeline",
    label: item.summary || item.event_type || "Timeline event",
    value: item.event_type || "event",
    tone: String(item.event_type || "").includes("disposition") ? "stale" : "muted",
    meta: item.occurred_at || "timestamp unavailable",
    at: activityTimestamp(item),
  };
}

function summarizeTask(item) {
  const tone = item.status === "completed" ? "ready" : item.due_at ? "stale" : "muted";
  const duePart = item.due_at ? `due ${item.due_at}` : "no due date";
  return {
    kind: "task",
    label: item.status === "completed" ? `Completed task: ${item.title}` : item.title || "Follow-up task",
    value: item.status || "open",
    tone,
    meta: `${item.priority || "normal"} priority | ${duePart}`,
    at: activityTimestamp(item),
  };
}

function summarizeNote(item) {
  const noteType = item.note_type || "internal";
  const isQualification = noteType === "qualification";
  const isManagerReview = noteType === "manager_review";
  const isManagerGuidance = noteType === "manager_guidance";
  return {
    kind: "note",
    label: isQualification
      ? "Lead qualification"
      : isManagerReview
        ? "Manager review"
        : isManagerGuidance
          ? "Manager guidance"
          : `Internal note: ${noteType}`,
    value: noteType,
    tone: noteType === "handoff" ? "stale" : noteType === "manager_guidance" ? "overdue" : "ready",
    meta: `${item.created_at || "timestamp unavailable"} | ${item.body || "Body unavailable"}`,
    at: activityTimestamp(item),
  };
}

export function buildCrmActivityStream(options = {}) {
  const settings = Object.assign(
    {
      messages: [],
      queueActions: [],
      timelineEvents: [],
      tasks: [],
      notes: [],
    },
    options
  );

  const messages = Array.isArray(settings.messages) ? settings.messages : [];
  const queueActions = Array.isArray(settings.queueActions) ? settings.queueActions : [];
  const timelineEvents = Array.isArray(settings.timelineEvents) ? settings.timelineEvents : [];
  const tasks = Array.isArray(settings.tasks) ? settings.tasks : [];
  const notes = Array.isArray(settings.notes) ? settings.notes : [];

  const filteredTimelineEvents = timelineEvents.filter((item) => {
    const eventType = String(item.event_type || "");
    return !["message_received", "message_sent", "task_created", "task_completed"].includes(eventType);
  });

  const rows = [
    ...messages.map(summarizeMessage),
    ...queueActions.map(summarizeQueueAction),
    ...filteredTimelineEvents.map(summarizeTimelineEvent),
    ...tasks.map(summarizeTask),
    ...notes.map(summarizeNote),
  ]
    .sort((left, right) => activityTimestamp(right).getTime() - activityTimestamp(left).getTime())
    .slice(0, 10);

  return {
    summaryCards: [
      {
        label: "Timeline entries",
        value: String(rows.length),
        detail: rows.length ? "Most recent CRM activity across this record." : "No CRM activity was available.",
        tone: rows.length ? "ready" : "muted",
      },
      {
        label: "Messages",
        value: String(messages.length),
        detail: messages.length ? "Inbound and outbound thread events." : "No message history returned.",
        tone: messages.length ? "ready" : "muted",
      },
      {
        label: "Task events",
        value: String(tasks.length),
        detail: tasks.length ? "Follow-up tasks are represented in the record stream." : "No follow-up tasks yet.",
        tone: tasks.length ? "stale" : "muted",
      },
      {
        label: notes.some((item) => item.note_type === "manager_review")
          ? "Manager review visible"
          : notes.some((item) => item.note_type === "qualification")
            ? "Qualification visible"
            : "Notes",
        value: String(notes.length),
        detail: notes.some((item) => item.note_type === "manager_review")
          ? "Manager review is visible to operators and owners."
          : notes.some((item) => item.note_type === "qualification")
            ? "Lead qualification is visible to managers and owners."
            : notes.length
              ? "Internal collaboration is represented in the record stream."
              : "No internal notes saved yet.",
        tone: notes.length ? "ready" : "muted",
      },
    ],
    rows: rows.length
      ? rows
      : [
          {
            kind: "empty",
            label: "CRM activity stream",
            value: "No record activity",
            tone: "muted",
            meta: "Messages, queue actions, task changes, and timeline events will appear here.",
          },
        ],
  };
}
